import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Box, Icon, Spinner, Text, useFormControl } from "@chakra-ui/react";
import { useSelect } from "downshift";
import { RemoveScroll } from "react-remove-scroll";
import isEqual from "lodash/isEqual";
import { useVirtualizer } from "@tanstack/react-virtual";
import { SelectOption } from "./select-option";
import { Popover, usePopover } from "./controlled-popover";
import ClearSelectButton from "./clear-select-button";
const defaultIsOptionDisabled = () => false;
function defaultOptionLabel(option) {
    return String(option.label ?? "");
}
function defaultOptionValue(option) {
    return option.value;
}
export function MultiSelect({ isDisabled = false, isLoading = false, isInvalid = false, isClearable = false, isOptionDisabled = defaultIsOptionDisabled, options, optionLabel = defaultOptionLabel, optionValue = defaultOptionValue, optionAccessory, placeholder = "", value, width = "xs", onChange, }) {
    const formControl = useFormControl({
        isDisabled: isDisabled || isLoading,
        isInvalid,
    });
    const { getTargetProps, getPopoverProps, update } = usePopover();
    const [isOpenScheduled, setIsOpenScheduled] = useState(false);
    const { isOpen, highlightedIndex, getToggleButtonProps, getMenuProps, getItemProps, openMenu, reset, } = useSelect({
        labelId: `${formControl.id}-label`,
        toggleButtonId: formControl.id,
        items: options,
        itemToString(option) {
            return option ? optionLabel(option) : "";
        },
        stateReducer(state, { type, changes }) {
            // Don't open and focus the menu immediately and schedule it for later.
            // Popover position must be recalculated first, otherwise Downshift
            // focuses the popover in its old position, causing the page to scroll
            // to the wrong position.
            // See https://floating-ui.com/docs/react-dom#effects.
            if (!state.isOpen && changes.isOpen) {
                if (!isOpenScheduled) {
                    update();
                    setIsOpenScheduled(true);
                    return state;
                }
                setIsOpenScheduled(false);
            }
            switch (type) {
                case useSelect.stateChangeTypes.MenuKeyDownEnter:
                case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
                case useSelect.stateChangeTypes.ItemClick:
                    return {
                        ...changes,
                        isOpen: true,
                        highlightedIndex: state.highlightedIndex,
                    };
                default:
                    return changes;
            }
        },
        selectedItem: null,
        onSelectedItemChange({ selectedItem }) {
            if (!selectedItem) {
                return;
            }
            const selectedValue = optionValue(selectedItem);
            const index = value.findIndex((valueItem) => {
                return isEqual(selectedValue, valueItem);
            });
            if (index > 0) {
                onChange([...value.slice(0, index), ...value.slice(index + 1)]);
            }
            else if (index === 0) {
                onChange([...value.slice(1)]);
            }
            else {
                onChange([...value, selectedValue]);
            }
        },
    });
    useLayoutEffect(() => {
        if (!isOpenScheduled) {
            return;
        }
        requestAnimationFrame(() => {
            openMenu();
        });
    }, [isOpenScheduled]);
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const keyDown = (event) => {
            if (event.key === "a" && event.metaKey) {
                event.preventDefault();
                const allValues = options.map((option) => optionValue(option));
                onChange(allValues);
            }
        };
        document.body.addEventListener("keydown", keyDown);
        return () => {
            document.body.removeEventListener("keydown", keyDown);
        };
    }, [isOpen, options, onChange]);
    const selectedOptions = useMemo(() => {
        const result = [];
        for (const valueItem of value) {
            const option = options.find((option) => {
                return isEqual(optionValue(option), valueItem);
            });
            if (option) {
                result.push(option);
            }
        }
        return result;
    }, [options, optionLabel, value]);
    let hoverBorderColor = "gray.500";
    if (isOpen) {
        hoverBorderColor = "gray.400";
    }
    if (isInvalid) {
        hoverBorderColor = "red.600";
    }
    const showClearButton = isClearable && value.length > 0;
    const clear = useCallback(() => {
        reset();
        onChange([]);
    }, [reset, onChange]);
    const optionsContainer = useRef(null);
    const virtualizer = useVirtualizer({
        count: options.length,
        getItemKey(index) {
            const option = options[index];
            return option ? optionLabel(option) : index;
        },
        getScrollElement() {
            return optionsContainer.current;
        },
        estimateSize() {
            return 37;
        },
    });
    useEffect(() => {
        virtualizer.scrollToIndex(highlightedIndex);
    }, [highlightedIndex]);
    return (React.createElement(RemoveScroll, { enabled: isOpen },
        React.createElement(Box, { as: "button", "aria-invalid": formControl["aria-invalid"], "aria-required": formControl["aria-required"], "aria-describedby": formControl["aria-describedby"], display: "flex", alignItems: "center", border: "1px", borderColor: isInvalid ? "red.500" : "gray.400", borderRadius: "md", transition: "200ms all", width: width, maxWidth: "100%", height: "36px", px: 3, disabled: isDisabled || isLoading, outline: "none", type: "button", _disabled: isLoading
                ? {
                    cursor: "wait",
                    _hover: {
                        borderColor: "gray.400",
                    },
                }
                : {
                    cursor: "not-allowed",
                    opacity: 0.4,
                    _hover: {
                        borderColor: "gray.400",
                    },
                }, _hover: {
                borderColor: hoverBorderColor,
            }, _focus: {
                borderColor: isInvalid ? "red.600" : "forest",
                // Fix app styles
                outline: "none !important",
            }, _focusVisible: {
                boxShadow: "outlineGreen",
            }, ...getToggleButtonProps(getTargetProps({
                onFocus: formControl.onFocus,
                onBlur: formControl.onBlur,
            })) },
            React.createElement(Text, { as: "span", flex: "1", minWidth: 0, fontSize: "md", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left", color: value ? "gray.800" : "gray.500", mr: 3 }, value.length > 0
                ? selectedOptions.map((option) => optionLabel(option)).join(", ")
                : placeholder),
            isLoading && (React.createElement(Spinner, { color: "forest", emptyColor: "gray.200", size: "sm", speed: "440ms", thickness: "2px" })),
            !isLoading &&
                (showClearButton ? (React.createElement(ClearSelectButton, { onClick: clear })) : (React.createElement(Icon, { as: ChevronDownIcon, boxSize: 4 })))),
        React.createElement(Popover, { isOpen: isOpen, ...getMenuProps(getPopoverProps()) },
            options.length === 0 && (React.createElement(Box, { py: 3, textAlign: "center" },
                React.createElement(Text, { color: "gray.600" }, "No options"))),
            options.length > 0 && (React.createElement(Box, { ref: optionsContainer, height: "100%", overflowY: "auto" },
                React.createElement(Box, { position: "relative", height: virtualizer.getTotalSize() }, virtualizer.getVirtualItems().map((virtualItem) => {
                    const option = options[virtualItem.index];
                    const isSelected = selectedOptions.includes(option);
                    return (React.createElement(Box, { key: virtualItem.key, position: "absolute", top: 0, left: 0, width: "100%", height: virtualItem.size, transform: `translateY(${virtualItem.start}px)` },
                        React.createElement(SelectOption, { isFocused: virtualItem.index === highlightedIndex, isSelected: isSelected, isDisabled: isOptionDisabled(option), label: optionLabel(option), accessory: typeof optionAccessory === "function"
                                ? optionAccessory(option)
                                : undefined, itemProps: getItemProps({
                                item: option,
                                index: virtualItem.index,
                                disabled: isOptionDisabled(option),
                            }) })));
                })))))));
}
